import { Clock, MessageSquare, User } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SessionMetadataInfoProps {
  /** Session creation timestamp (ISO string) */
  createdAt: string
  /** Number of messages in the session */
  messageCount: number
  /** Display name of user who created the session */
  createdByName?: string
  /** Email of user who created the session */
  createdByEmail?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Format date for display (e.g., "Dec 25, 14:28")
 */
function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

/**
 * Displays session metadata including creation time and message count.
 *
 * @example
 * ```tsx
 * <SessionMetadataInfo
 *   createdAt="2025-12-25T14:28:30Z"
 *   messageCount={5}
 * />
 * ```
 */
export function SessionMetadataInfo({
  createdAt,
  messageCount,
  createdByName,
  createdByEmail,
  className
}: SessionMetadataInfoProps) {
  const authorDisplay = createdByName || createdByEmail

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>Session Info</span>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Created:</span>
          <span>{formatDate(createdAt)}</span>
        </div>
        {authorDisplay && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Started by:</span>
            <span className="flex items-center gap-1 truncate max-w-[140px]" title={createdByEmail || createdByName}>
              <User className="h-3 w-3 flex-shrink-0" />
              {authorDisplay}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Messages:</span>
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {messageCount}
          </span>
        </div>
      </div>
    </div>
  )
}
